import { useState, useEffect, useCallback } from "react";

// ── Config ────────────────────────────────────────────────────────────────────
const REPORT_API = "/api/pan/reports/";          // GET list
const DETAIL_API = "/api/pan/reports/";          // GET detail by id
const REGEN_API  = "/api/pan/regenerate/";       // POST regenerate PDFs

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS = {
  pending:  { label:"Pending",   bg:"var(--bg-warning)",  text:"var(--text-warning)",  icon:"ti-clock"         },
  success:  { label:"Success",   bg:"var(--bg-success)",  text:"var(--text-success)",  icon:"ti-circle-check"  },
  failed:   { label:"Failed",    bg:"var(--bg-danger)",   text:"var(--text-danger)",   icon:"ti-circle-x"      },
  new:      { label:"New",       bg:"var(--bg-accent)",   text:"var(--text-accent)",   icon:"ti-plus"          },
  complete: { label:"Complete",  bg:"var(--bg-success)",  text:"var(--text-success)",  icon:"ti-check"         },
};

const TYPE = {
  new:    { label:"New PAN",    icon:"ti-plus",   bg:"var(--bg-accent)"  },
  change: { label:"Change PAN", icon:"ti-edit",   bg:"var(--bg-warning)" },
};

const getStatus = s => STATUS[String(s).toLowerCase()] || STATUS.pending;
const getType   = t => TYPE[String(t).toLowerCase()]   || TYPE.new;

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = `
  .rpt-wrap{font-family:var(--font-sans,"Segoe UI",system-ui,sans-serif);color:var(--text-primary);background:var(--surface-0);min-height:100vh;font-size:14px}
  .rpt-hdr{background:var(--surface-2);border-bottom:0.5px solid var(--border-strong);padding:14px 24px;display:flex;align-items:center;gap:12px;position:sticky;top:0;z-index:50}
  .rpt-hdr-logo{width:32px;height:32px;background:var(--fill-accent);border-radius:8px;display:flex;align-items:center;justify-content:center;color:var(--on-accent);font-size:16px;flex-shrink:0}
  .rpt-hdr-title{font-size:15px;font-weight:500}
  .rpt-hdr-sub{font-size:12px;color:var(--text-muted)}
  .rpt-hdr-right{margin-left:auto;display:flex;align-items:center;gap:8px}
  .rpt-body{max-width:1100px;margin:0 auto;padding:20px 16px 60px}

  /* Stats row */
  .stats-row{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px}
  .stat-card{background:var(--surface-2);border:0.5px solid var(--border);border-radius:var(--radius);padding:14px 16px}
  .stat-lbl{font-size:11px;color:var(--text-muted);margin-bottom:6px;font-weight:500}
  .stat-val{font-size:24px;font-weight:500;color:var(--text-primary);line-height:1}
  .stat-sub{font-size:11px;color:var(--text-muted);margin-top:4px}
  @media(max-width:600px){.stats-row{grid-template-columns:1fr 1fr}}

  /* Toolbar */
  .toolbar{display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap;align-items:center}
  .search-box{flex:1;min-width:180px;position:relative}
  .search-box i{position:absolute;left:10px;top:50%;transform:translateY(-50%);color:var(--text-muted);font-size:15px;pointer-events:none}
  .search-input{width:100%;height:36px;padding:0 10px 0 34px;border:0.5px solid var(--border-strong);border-radius:var(--radius);font-size:13px;background:var(--fill-field);color:var(--text-primary);font-family:inherit;box-sizing:border-box}
  .search-input:focus{outline:none;border-color:var(--fill-accent);box-shadow:0 0 0 2px var(--bg-accent)}
  .filter-sel{height:36px;padding:0 10px;border:0.5px solid var(--border-strong);border-radius:var(--radius);font-size:13px;background:var(--fill-field);color:var(--text-primary);font-family:inherit}
  .filter-sel:focus{outline:none;border-color:var(--fill-accent)}
  .btn-icon{height:36px;padding:0 12px;border:0.5px solid var(--border-strong);border-radius:var(--radius);background:var(--surface-2);color:var(--text-primary);cursor:pointer;font-family:inherit;font-size:13px;display:flex;align-items:center;gap:6px;transition:all .15s}
  .btn-icon:hover{background:var(--surface-1)}
  .btn-accent{background:var(--fill-accent);color:var(--on-accent);border-color:transparent}
  .btn-accent:hover{opacity:.9}

  /* Table */
  .tbl-wrap{background:var(--surface-2);border:0.5px solid var(--border);border-radius:12px;overflow:hidden}
  .tbl{width:100%;border-collapse:collapse}
  .tbl th{padding:10px 14px;text-align:left;font-size:11px;font-weight:500;color:var(--text-muted);text-transform:uppercase;letter-spacing:.5px;background:var(--surface-1);border-bottom:0.5px solid var(--border)}
  .tbl td{padding:12px 14px;font-size:13px;border-bottom:0.5px solid var(--border);vertical-align:middle}
  .tbl tr:last-child td{border-bottom:none}
  .tbl tr:hover td{background:var(--surface-1)}
  .tbl tr{cursor:pointer;transition:background .1s}

  /* Badges */
  .badge{display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:20px;font-size:11px;font-weight:500}
  .type-badge{display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:20px;font-size:11px;font-weight:500}

  /* Pagination */
  .pager{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-top:0.5px solid var(--border);font-size:12px;color:var(--text-muted);background:var(--surface-1)}
  .pager-btns{display:flex;gap:4px}
  .pager-btn{height:28px;min-width:28px;padding:0 8px;border:0.5px solid var(--border-strong);border-radius:6px;background:var(--surface-2);color:var(--text-primary);cursor:pointer;font-size:12px;display:flex;align-items:center;justify-content:center}
  .pager-btn:hover:not(:disabled){background:var(--surface-1)}
  .pager-btn:disabled{opacity:.4;cursor:not-allowed}
  .pager-btn.active{background:var(--fill-accent);color:var(--on-accent);border-color:transparent}

  /* Detail drawer */
  .drawer-bg{position:fixed;inset:0;background:rgba(0,0,0,.35);z-index:100;display:flex;justify-content:flex-end}
  .drawer{width:480px;max-width:95vw;height:100%;background:var(--surface-2);border-left:0.5px solid var(--border);overflow-y:auto;display:flex;flex-direction:column}
  .drawer-head{padding:16px 20px;border-bottom:0.5px solid var(--border);display:flex;align-items:center;gap:10px;position:sticky;top:0;background:var(--surface-2);z-index:5}
  .drawer-title{font-size:15px;font-weight:500;flex:1}
  .drawer-body{padding:20px;flex:1}
  .dl-row{display:flex;padding:8px 0;border-bottom:0.5px solid var(--border);font-size:13px}
  .dl-row:last-child{border-bottom:none}
  .dl-lbl{color:var(--text-muted);width:140px;flex-shrink:0;font-size:12px;padding-top:1px}
  .dl-val{color:var(--text-primary);font-weight:500;flex:1;word-break:break-all}
  .sec-head{font-size:11px;font-weight:500;color:var(--text-muted);text-transform:uppercase;letter-spacing:.6px;margin:18px 0 8px;padding-bottom:6px;border-bottom:0.5px solid var(--border)}
  .pdf-links{display:flex;flex-wrap:wrap;gap:8px;margin-top:6px}
  .pdf-link{display:inline-flex;align-items:center;gap:6px;padding:8px 12px;background:var(--surface-1);border:0.5px solid var(--border);border-radius:var(--radius);text-decoration:none;font-size:12px;font-weight:500;color:var(--text-primary);transition:all .15s}
  .pdf-link:hover{border-color:var(--fill-accent);color:var(--text-accent)}
  .pdf-link.primary{background:var(--fill-accent);color:var(--on-accent);border-color:transparent}
  .pdf-link.primary:hover{opacity:.9}
  .regen-btn{width:100%;height:38px;margin-top:14px;background:var(--surface-1);border:0.5px solid var(--border-strong);border-radius:var(--radius);color:var(--text-primary);font-size:13px;font-weight:500;cursor:pointer;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .15s}
  .regen-btn:hover{background:var(--bg-accent);border-color:var(--border-accent);color:var(--text-accent)}

  /* Empty / loading */
  .empty{text-align:center;padding:48px 20px;color:var(--text-muted)}
  .empty i{font-size:40px;display:block;margin-bottom:10px;opacity:.4}
  .empty p{font-size:14px;margin:0}
  .skeleton{background:var(--surface-1);border-radius:4px;animation:shimmer 1.2s infinite}
  @keyframes shimmer{0%,100%{opacity:.6}50%{opacity:1}}

  /* Spinner */
  .spin{animation:spin .7s linear infinite}
  @keyframes spin{to{transform:rotate(360deg)}}

  /* avatar */
  .avatar{width:32px;height:32px;border-radius:50%;background:var(--bg-accent);color:var(--text-accent);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:500;flex-shrink:0}
`;

// ── Skeleton row ──────────────────────────────────────────────────────────────
const SkRow = () => (
  <tr>
    {[140,90,100,80,90,100].map((w,i) => (
      <td key={i}><div className="skeleton" style={{height:14,width:w,borderRadius:4}} /></td>
    ))}
  </tr>
);

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = getStatus(status);
  return (
    <span className="badge" style={{background:s.bg,color:s.text}}>
      <i className={`ti ${s.icon}`} aria-hidden style={{fontSize:12}} />
      {s.label}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const t = getType(type);
  return (
    <span className="type-badge" style={{background:t.bg,color:"var(--text-primary)"}}>
      <i className={`ti ${t.icon}`} aria-hidden style={{fontSize:11}} />
      {t.label}
    </span>
  );
};

// ── Initials avatar ───────────────────────────────────────────────────────────
const Avatar = ({ name="" }) => {
  const parts = name.trim().split(/\s+/);
  const init  = parts.length>=2 ? parts[0][0]+parts[1][0] : name.slice(0,2);
  return <div className="avatar">{init.toUpperCase()}</div>;
};

// ── Detail drawer ─────────────────────────────────────────────────────────────
const Drawer = ({ record, onClose, onRegen, regenLoading }) => {
  if (!record) return null;
  const tok = localStorage.getItem("auth_token")||"";

  const fields = [
    ["Reference no.",   record.reference_num || record.id],
    ["Full name",       record.fullname],
    ["Father's name",   record.fathername],
    ["Mobile",          record.mobile],
    ["Email",           record.email],
    ["Date of birth",   record.dob],
    ["Gender",          record.gender],
    ["Aadhaar no.",     record.aadharnum ? record.aadharnum.replace(/(\d{4})(?=\d)/g,"$1 ") : "—"],
    ["District",        record.res_district || "—"],
    ["State",           record.res_state || "—"],
    ["PIN code",        record.res_pin || "—"],
    ["Income sources",  Array.isArray(record.income_sources) ? record.income_sources.join(", ") : record.income_sources || "—"],
    ["Application no.", record.application_number || "—"],
    ["Submitted on",    record.created_at ? new Date(record.created_at).toLocaleString("en-IN") : "—"],
  ];

  const pdfs = [
    { label:"Form 93",     url:record.pdf_form93,  icon:"ti-file-text",    primary:true  },
    { label:"Form CR",     url:record.pdf_formcr,  icon:"ti-file-text",    primary:false },
    { label:"Full bundle", url:record.pdf_bundle,  icon:"ti-package",      primary:true  },
    { label:"Slip",        url:record.pdf_slip,    icon:"ti-receipt",      primary:false },
    { label:"Birth cert",  url:record.birth_cert,  icon:"ti-certificate",  primary:false },
  ].filter(p => p.url);

  return (
    <div className="drawer-bg" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="drawer">
        <div className="drawer-head">
          <Avatar name={record.fullname||"?"} />
          <div className="drawer-title">{record.fullname||"Application"}</div>
          <div style={{display:"flex",gap:4}}>
            <TypeBadge type={record.application_type||"new"} />
            <StatusBadge status={record.status||"pending"} />
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:"var(--text-muted)",padding:4,borderRadius:6}}>
            <i className="ti ti-x" aria-hidden style={{fontSize:18}} />
          </button>
        </div>

        <div className="drawer-body">
          <div className="sec-head">Applicant details</div>
          <div style={{background:"var(--surface-1)",borderRadius:"var(--radius)",border:"0.5px solid var(--border)",padding:"4px 0",marginBottom:4}}>
            {fields.map(([l,v]) => (
              <div key={l} className="dl-row" style={{padding:"8px 14px"}}>
                <span className="dl-lbl">{l}</span>
                <span className="dl-val">{v||"—"}</span>
              </div>
            ))}
          </div>

          {pdfs.length>0 && (
            <>
              <div className="sec-head">Documents</div>
              <div className="pdf-links">
                {pdfs.map(p => (
                  <a key={p.label} href={p.url} target="_blank" rel="noreferrer"
                    className={`pdf-link${p.primary?" primary":""}`}>
                    <i className={`ti ${p.icon}`} aria-hidden style={{fontSize:14}} />
                    {p.label}
                  </a>
                ))}
              </div>
            </>
          )}

          <div className="sec-head">Actions</div>
          <button className="regen-btn" onClick={()=>onRegen(record.id)} disabled={regenLoading}>
            <i className={`ti ti-refresh ${regenLoading?"spin":""}`} aria-hidden style={{fontSize:16}} />
            {regenLoading ? "Regenerating PDFs…" : "Regenerate PDFs"}
          </button>

          {record.pdf_form93 && (
            <a href={record.pdf_form93} target="_blank" rel="noreferrer" className="regen-btn" style={{textDecoration:"none",marginTop:8,background:"var(--bg-success)",borderColor:"var(--border-success)",color:"var(--text-success)"}}>
              <i className="ti ti-eye" aria-hidden style={{fontSize:16}} />
              Preview filled form
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
export default function PanCardReports() {
  const [records,     setRecords]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [statusFilter,setStatusFilter]= useState("");
  const [typeFilter,  setTypeFilter]  = useState("");
  const [page,        setPage]        = useState(1);
  const [total,       setTotal]       = useState(0);
  const [selected,    setSelected]    = useState(null);
  const [regenLoad,   setRegenLoad]   = useState(false);
  const [alert,       setAlert]       = useState(null);
  const PER_PAGE = 15;

  // ── Fetch records ───────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tok = localStorage.getItem("auth_token")||"";
      const params = new URLSearchParams({
        page,
        per_page: PER_PAGE,
        ...(search      ? {search}      : {}),
        ...(statusFilter? {status:statusFilter} : {}),
        ...(typeFilter  ? {application_type:typeFilter} : {}),
      });
      const r = await fetch(`${REPORT_API}?${params}`,{
        headers: tok ? {"Authorization":"Token "+tok} : {},
      });
      const j = await r.json();
      // Handle both {results:[], count:N} and {data:[], total:N} response shapes
      setRecords(j.results || j.data || j.records || []);
      setTotal(j.count || j.total || (j.results||j.data||j.records||[]).length);
    } catch(e) {
      setAlert({type:"err", msg:"Failed to load reports: "+e.message});
      // Demo fallback data for UI preview
      setRecords(DEMO_DATA);
      setTotal(DEMO_DATA.length);
    }
    setLoading(false);
  }, [page, search, statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── Regenerate PDFs ─────────────────────────────────────────────────────────
  const regen = async (id) => {
    setRegenLoad(true);
    try {
      const tok = localStorage.getItem("auth_token")||"";
      const r = await fetch(`${REGEN_API}${id}/`, {
        method:"POST",
        headers: tok ? {"Authorization":"Token "+tok} : {},
      });
      const j = await r.json();
      if (j.status) {
        setAlert({type:"ok", msg:"PDFs regenerated successfully"});
        // Refresh the selected record with new URLs
        setSelected(prev => ({...prev, ...j}));
        load();
      } else {
        setAlert({type:"err", msg:j.data||"Regeneration failed"});
      }
    } catch(e) {
      setAlert({type:"err", msg:"Error: "+e.message});
    }
    setRegenLoad(false);
  };

  // ── Stats ───────────────────────────────────────────────────────────────────
  const stats = {
    total:   records.length,
    new:     records.filter(r=>r.application_type==="new").length,
    change:  records.filter(r=>r.application_type==="change").length,
    success: records.filter(r=>String(r.status).toLowerCase()==="success").length,
  };

  const totalPages = Math.ceil(total / PER_PAGE);

  // ── Filter records client-side for demo ─────────────────────────────────────
  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (r.fullname||"").toLowerCase().includes(q) ||
      (r.mobile||"").includes(q) ||
      (r.aadharnum||"").includes(q) ||
      (r.reference_num||"").includes(q) ||
      (r.application_number||"").includes(q);
    const matchStatus = !statusFilter || String(r.status).toLowerCase()===statusFilter;
    const matchType   = !typeFilter   || r.application_type===typeFilter;
    return matchSearch&&matchStatus&&matchType;
  });

  return (
    <div className="rpt-wrap">
      <style>{styles}</style>

      {/* Header */}
      <div className="rpt-hdr">
        <div className="rpt-hdr-logo"><i className="ti ti-id-badge" aria-hidden /></div>
        <div>
          <div className="rpt-hdr-title">PAN card applications</div>
          <div className="rpt-hdr-sub">Shree Ram Enterprises · Agent 87520</div>
        </div>
        <div className="rpt-hdr-right">
          <button className="btn-icon" onClick={load} title="Refresh">
            <i className={`ti ti-refresh ${loading?"spin":""}`} aria-hidden style={{fontSize:16}} />
          </button>
          <button className="btn-icon btn-accent" onClick={()=>window.location.href="/pan/new"}>
            <i className="ti ti-plus" aria-hidden style={{fontSize:15}} /> New application
          </button>
        </div>
      </div>

      <div className="rpt-body">

        {/* Alert */}
        {alert && (
          <div style={{padding:"10px 14px",borderRadius:"var(--radius)",marginBottom:14,fontSize:12,
            background:alert.type==="ok"?"var(--bg-success)":"var(--bg-danger)",
            color:alert.type==="ok"?"var(--text-success)":"var(--text-danger)",
            border:`0.5px solid ${alert.type==="ok"?"var(--border-success)":"var(--border-danger)"}`}}>
            <i className={`ti ${alert.type==="ok"?"ti-circle-check":"ti-alert-triangle"}`} aria-hidden style={{marginRight:6}} />
            {alert.msg}
            <button onClick={()=>setAlert(null)} style={{float:"right",background:"none",border:"none",cursor:"pointer",color:"inherit",fontSize:14,lineHeight:1}}>×</button>
          </div>
        )}

        {/* Stats */}
        <div className="stats-row">
          {[
            { label:"Total applications", value:total,         sub:"all time",          icon:"ti-files",       color:"var(--text-accent)"   },
            { label:"New PAN",            value:stats.new,     sub:"first-time applies", icon:"ti-plus",        color:"var(--text-accent)"   },
            { label:"Change / correct",   value:stats.change,  sub:"corrections",        icon:"ti-edit",        color:"var(--text-warning)"  },
            { label:"Completed",          value:stats.success, sub:"PDFs generated",     icon:"ti-circle-check",color:"var(--text-success)"  },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-lbl" style={{display:"flex",alignItems:"center",gap:5}}>
                <i className={`ti ${s.icon}`} aria-hidden style={{fontSize:14,color:s.color}} /> {s.label}
              </div>
              <div className="stat-val" style={{color:s.color}}>{loading?"—":s.value}</div>
              <div className="stat-sub">{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="toolbar">
          <div className="search-box">
            <i className="ti ti-search" aria-hidden />
            <input className="search-input" placeholder="Search by name, mobile, Aadhaar, reference…"
              value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} />
          </div>
          <select className="filter-sel" value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setPage(1);}}>
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="new">New</option>
          </select>
          <select className="filter-sel" value={typeFilter} onChange={e=>{setTypeFilter(e.target.value);setPage(1);}}>
            <option value="">All types</option>
            <option value="new">New PAN</option>
            <option value="change">Change PAN</option>
          </select>
          {(search||statusFilter||typeFilter) && (
            <button className="btn-icon" onClick={()=>{setSearch("");setStatusFilter("");setTypeFilter("");setPage(1);}}>
              <i className="ti ti-x" aria-hidden style={{fontSize:14}} /> Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Reference</th>
                <th>Type</th>
                <th>Mobile</th>
                <th>District / State</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Documents</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array(6).fill(0).map((_,i)=><SkRow key={i} />)
                : filtered.length===0
                  ? (
                    <tr><td colSpan={8}>
                      <div className="empty">
                        <i className="ti ti-file-off" aria-hidden />
                        <p>No applications found{search?" for that search":""}</p>
                      </div>
                    </td></tr>
                  )
                  : filtered.map(r => (
                    <tr key={r.id||r.reference_num} onClick={()=>setSelected(r)}>
                      <td>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <Avatar name={r.fullname||"?"} />
                          <div>
                            <div style={{fontWeight:500,fontSize:13}}>{r.fullname||"—"}</div>
                            <div style={{fontSize:11,color:"var(--text-muted)"}}>{r.email||r.aadharnum?.slice(-4)?.padStart(12,"•••• •••• ")}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{fontFamily:"var(--font-mono,'Courier New')",fontSize:12,color:"var(--text-accent)",fontWeight:500}}>
                          #{r.reference_num||r.id||"—"}
                        </div>
                        {r.application_number&&<div style={{fontSize:11,color:"var(--text-muted)"}}>App: {r.application_number}</div>}
                      </td>
                      <td><TypeBadge type={r.application_type||"new"} /></td>
                      <td style={{color:"var(--text-secondary)",fontSize:12}}>{r.mobile||"—"}</td>
                      <td>
                        <div style={{fontSize:12}}>{r.res_district||"—"}</div>
                        <div style={{fontSize:11,color:"var(--text-muted)"}}>{r.res_state||""}</div>
                      </td>
                      <td style={{fontSize:12,color:"var(--text-muted)",whiteSpace:"nowrap"}}>
                        {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"2-digit"}) : "—"}
                      </td>
                      <td><StatusBadge status={r.status||"pending"} /></td>
                      <td>
                        <div style={{display:"flex",gap:4}}>
                          {r.pdf_form93 && <a href={r.pdf_form93} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} title="Form 93" style={{color:"var(--text-accent)",fontSize:16}}><i className="ti ti-file-text" aria-hidden /></a>}
                          {r.pdf_formcr && <a href={r.pdf_formcr} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} title="Form CR"  style={{color:"var(--text-accent)",fontSize:16}}><i className="ti ti-file-text" aria-hidden /></a>}
                          {r.pdf_bundle && <a href={r.pdf_bundle} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} title="Bundle"   style={{color:"var(--text-success)",fontSize:16}}><i className="ti ti-package" aria-hidden /></a>}
                          {!r.pdf_form93&&!r.pdf_bundle&&<span style={{fontSize:12,color:"var(--text-muted)"}}>—</span>}
                        </div>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pager">
            <span>Showing {filtered.length} of {total} applications</span>
            <div className="pager-btns">
              <button className="pager-btn" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>
                <i className="ti ti-chevron-left" aria-hidden style={{fontSize:13}} />
              </button>
              {Array.from({length:Math.min(totalPages,7)},(_,i)=>{
                const p = totalPages<=7 ? i+1 : page<=4 ? i+1 : page>=totalPages-3 ? totalPages-6+i : page-3+i;
                return (
                  <button key={p} className={`pager-btn${p===page?" active":""}`} onClick={()=>setPage(p)}>{p}</button>
                );
              })}
              <button className="pager-btn" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>
                <i className="ti ti-chevron-right" aria-hidden style={{fontSize:13}} />
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Detail drawer */}
      {selected && (
        <Drawer record={selected} onClose={()=>setSelected(null)} onRegen={regen} regenLoading={regenLoad} />
      )}
    </div>
  );
}

// ── Demo data (shown when API unavailable) ────────────────────────────────────
const DEMO_DATA = [
  {id:1,reference_num:"9834712050",application_number:"APP001",fullname:"KUMKUM MAURYA",email:"kumkum@example.com",mobile:"916394856756",dob:"2001-12-03",gender:"Female",aadharnum:"674589345623",res_district:"Lucknow",res_state:"Uttar Pradesh",res_pin:"226001",application_type:"new",status:"success",income_sources:["no_income"],pdf_form93:"/media/form93.pdf",pdf_bundle:"/media/bundle.pdf",created_at:"2026-07-01T10:23:00Z"},
  {id:2,reference_num:"7621034891",application_number:"APP002",fullname:"RAM PRASAD MAURYA",email:"ram@example.com",mobile:"919876543210",dob:"1978-04-15",gender:"Male",aadharnum:"123456789012",res_district:"Agra",res_state:"Uttar Pradesh",res_pin:"282001",application_type:"change",status:"pending",income_sources:["salary","business"],pdf_formcr:"/media/formcr.pdf",created_at:"2026-07-01T11:45:00Z"},
  {id:3,reference_num:"5512893047",application_number:"APP003",fullname:"SUNITA DEVI",email:"sunita@example.com",mobile:"917654321098",dob:"1990-07-22",gender:"Female",aadharnum:"987654321098",res_district:"Varanasi",res_state:"Uttar Pradesh",res_pin:"221001",application_type:"new",status:"failed",income_sources:["salary"],created_at:"2026-06-30T09:00:00Z"},
  {id:4,reference_num:"3309182756",application_number:"APP004",fullname:"AMIT SHARMA",email:"amit@example.com",mobile:"918765432109",dob:"1985-02-10",gender:"Male",aadharnum:"567891234560",res_district:"Kanpur",res_state:"Uttar Pradesh",res_pin:"208001",application_type:"new",status:"success",income_sources:["business"],pdf_form93:"/media/form93_4.pdf",pdf_bundle:"/media/bundle_4.pdf",created_at:"2026-06-29T14:30:00Z"},
  {id:5,reference_num:"8821047563",application_number:"APP005",fullname:"PRIYA GUPTA",email:"priya@example.com",mobile:"919654321078",dob:"1995-11-05",gender:"Female",aadharnum:"345678901234",res_district:"Noida",res_state:"Uttar Pradesh",res_pin:"201301",application_type:"change",status:"success",income_sources:["house_property"],pdf_formcr:"/media/formcr_5.pdf",pdf_bundle:"/media/bundle_5.pdf",created_at:"2026-06-28T16:15:00Z"},
];